/**
 * HeyGen Service - Handles communication with HeyGen API via TheyChat Backend
 *
 * Backend Route:
 * - POST /heygen/session-token - Get session token for LiveAvatar streaming
 *
 * NO MOCK FALLBACKS - All errors are properly handled and logged
 * Check browser DevTools Console (F12) to see error logs
 */

import { logger } from '../utils/logger';

export const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://api.theychat.app').replace(
  /\/$/,
  '',
);

// Timeout constants
export const REQUEST_TIMEOUT = 15000; // 15 seconds for API calls
export const INIT_TIMEOUT = 30000; // 30 seconds for avatar initialization

/**
 * Fetch with timeout using AbortController
 * Automatically aborts request if it takes too long
 */
export const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeout: number = REQUEST_TIMEOUT,
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout / 1000} seconds`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Types
export interface HeyGenSessionTokenResponse {
  token: string;
  expiresAt: string;
  maxDurationSeconds?: number;
  credits?: {
    available: number;
    ratePerBlock: number;
    blockSeconds: number;
  };
  message?: string;
}

export interface HeyGenError {
  error: string;
  currentTier?: string;
}

// Helper to get auth headers (same pattern as chatService)
export const getAuthHeaders = (): Record<string, string> => {
  const idToken = localStorage.getItem('idToken');
  const language = localStorage.getItem('theychat-language') || 'en';

  if (!idToken) {
    logger.warn('HeyGenService', 'No authentication token found in localStorage');
  }

  return {
    'Content-Type': 'application/json',
    'Accept-Language': language,
    ...(idToken && { Authorization: `Bearer ${idToken}` }),
  };
};

// API Error handling
class HeyGenApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public currentTier?: string,
  ) {
    super(message);
    this.name = 'HeyGenApiError';
  }
}

/**
 * Check if user has access to HeyGen LiveAvatar
 * Uses dedicated /access endpoint that only checks subscription tier
 * Returns true if user has premium or diamond tier
 */
export const checkHeyGenAccess = async (): Promise<{ hasAccess: boolean; tier: string }> => {
  logger.debug('HeyGenService', 'Checking HeyGen access...');

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/heygen/access`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        logger.warn('HeyGenService', 'Unauthorized - user not logged in');
        return { hasAccess: false, tier: 'free' };
      }
      throw new Error(`Access check failed: ${response.status}`);
    }

    const data: { hasAccess: boolean; tier: string } = await response.json();
    logger.info('HeyGenService', 'Access check result', data);
    return data;
  } catch (error) {
    logger.error(
      'HeyGenService',
      'Failed to check access',
      error instanceof Error ? error : new Error(String(error)),
    );
    return { hasAccess: false, tier: 'free' };
  }
};

/**
 * Get HeyGen session token for LiveAvatar streaming
 * Requires premium or diamond subscription tier
 * @param avatarId - ID of the avatar to start session with
 * @throws {HeyGenApiError} When API call fails
 */
export const getHeyGenSessionToken = async (
  avatarId: string,
): Promise<HeyGenSessionTokenResponse> => {
  logger.debug('HeyGenService', 'Requesting session token...', {
    apiUrl: API_BASE_URL,
    avatarId,
  });

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/heygen/session-token`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ avatarId }),
    });

    if (!response.ok) {
      // Try to parse error response
      const errorData: HeyGenError = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));

      const apiError = new HeyGenApiError(
        errorData.error || 'Failed to get HeyGen session token',
        response.status,
        errorData.currentTier,
      );

      // Log based on error type
      if (response.status === 401) {
        logger.error(
          'HeyGenService',
          'Authentication failed - user not logged in or token expired',
          apiError,
        );
      } else if (response.status === 403) {
        logger.warn('HeyGenService', 'Access denied - subscription tier insufficient', {
          currentTier: errorData.currentTier,
          requiredTier: 'premium or diamond',
        });
      } else if (response.status >= 500) {
        logger.error('HeyGenService', 'Server error from HeyGen API', apiError, {
          statusCode: response.status,
        });
      } else {
        logger.error('HeyGenService', 'Failed to get session token', apiError, {
          statusCode: response.status,
          errorMessage: errorData.error,
        });
      }

      throw apiError;
    }

    const tokenResponse: HeyGenSessionTokenResponse = await response.json();

    logger.info('HeyGenService', 'Session token obtained successfully', {
      expiresAt: tokenResponse.expiresAt,
    });

    return tokenResponse;
  } catch (error) {
    // Re-throw HeyGenApiError as-is (already logged above)
    if (error instanceof HeyGenApiError) {
      throw error;
    }

    // Handle network errors (no internet, CORS, timeout, etc.)
    const networkError = new HeyGenApiError(
      error instanceof Error ? error.message : 'Failed to connect to HeyGen API',
      0, // 0 indicates network error (not HTTP error)
    );

    logger.error('HeyGenService', 'Network error - could not reach API', networkError, {
      originalError: error instanceof Error ? error.message : String(error),
      apiUrl: API_BASE_URL,
      possibleCauses: [
        'No internet connection',
        'API server is down',
        'CORS policy blocking request',
        'Request timeout',
      ],
    });

    throw networkError;
  }
};

/**
 * Get TTS Audio from Backend (Custom Mode)
 * Returns Base64 encoded audio string (PCM 16bit 24kHz)
 */
export const getTTSAudio = async (text: string): Promise<string> => {
  logger.debug('HeyGenService', 'Requesting TTS audio...');
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/heygen/tts`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      throw new Error(`TTS request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.audio;
  } catch (error) {
    logger.error(
      'HeyGenService',
      'Failed to get TTS audio',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw error;
  }
};

/**
 * Get HeyGen session token with retry logic
 * Uses exponential backoff for transient errors
 * Does NOT retry auth errors (401, 403)
 *
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param baseDelay - Base delay in ms for exponential backoff (default: 1000)
 */
export const getHeyGenSessionTokenWithRetry = async (
  avatarId: string,
  maxRetries: number = 3,
  baseDelay: number = 1000,
): Promise<HeyGenSessionTokenResponse> => {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      logger.debug('HeyGenService', `Token request attempt ${attempt}/${maxRetries}`);
      return await getHeyGenSessionToken(avatarId);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry authentication/authorization errors - they won't succeed
      if (error instanceof HeyGenApiError) {
        if (error.statusCode === 401 || error.statusCode === 403) {
          logger.debug('HeyGenService', 'Not retrying auth error', {
            statusCode: error.statusCode,
          });
          throw error;
        }
      }

      // Log retry attempt (if not last attempt)
      if (attempt < maxRetries) {
        const delay = baseDelay * 2 ** (attempt - 1); // 1s, 2s, 4s
        logger.warn(
          'HeyGenService',
          `Retry attempt ${attempt}/${maxRetries} failed, retrying in ${delay}ms`,
          {
            error: lastError.message,
            nextAttemptIn: `${delay}ms`,
          },
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        logger.error('HeyGenService', `All ${maxRetries} retry attempts failed`, lastError);
      }
    }
  }

  throw lastError || new Error('Failed to get session token after retries');
};

// Queue methods have been moved to heygenQueueService.ts

export { HeyGenApiError };
